import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { Field } from '../../UI';
import { formatCpfCnpj } from '../../../../helpers/utils/utils';

/**
 * Dados cadastrais da aba Cliente (AW2-1136 Etapa 1) — porta de
 * `IdentificacaoCliente.jsx` real, 18 campos, **sempre somente-leitura**
 * (confirmado por leitura direta — nenhum campo de `IdentificacaoCliente.jsx`
 * real depende de um modo de edição, diferente de `IdentificacaoDevedor.jsx`).
 * Agrupamento visual em linhas aproxima o real (Código/Nome/Origem
 * Lead/Status; Nome Fantasia/Inscrição Estadual/Inscrição Municipal;
 * Categoria/CPF-CNPJ/Preposto/Identificação Interna; Filial
 * Responsável/Ramos de Atividade; Comercial Principal/Telemarketing;
 * Observação) sem replicar as proporções exatas de coluna do `Grid24` real
 * (24 colunas), que não mapeiam 1:1 para o grid CSS do protótipo.
 *
 * CPF/CNPJ sempre formatado via `formatCpfCnpj` (mesmo utilitário puro já
 * usado em `IdentificacaoDevedor.jsx`) — sem a ramificação "cru durante
 * edição" que Devedor precisa, porque este campo nunca é editado aqui.
 *
 * `categoria` aqui já é uma STRING PRONTA PARA EXIBIÇÃO no DTO real
 * (`cliente?.categoria`, lida direto por um `InputField disabled` simples em
 * produção) — diferente do campo homônimo de Devedor, que é uma string enum
 * a ser casada com as opções de um select. Não usar `tipo="select"` aqui.
 */
export default function IdentificacaoCliente({ formFields }) {
  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field
          label="Código:"
          value={formFields?.idCliente != null && formFields?.idCliente !== ''
            ? Number(formFields.idCliente).toLocaleString('pt-BR', { minimumFractionDigits: 0 })
            : ''}
          disabled
        />
        <Field label="Nome:" value={formFields?.nomeCliente ?? ''} disabled />
        <Field label="Origem Lead:" value={formFields?.origemLead ?? ''} disabled />
        <Field label="Status:" value={formFields?.statusCliente ?? ''} disabled />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mt: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field label="Nome Fantasia:" value={formFields?.nomeFantasia ?? ''} disabled />
        <Field label="Inscrição Estadual:" value={formFields?.inscricaoEstadual ?? ''} disabled />
        <Field label="Inscrição Municipal:" value={formFields?.inscricaoMunicipal ?? ''} disabled />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mt: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field label="Categoria:" value={formFields?.categoria ?? ''} disabled />
        <Field
          label="CPF/CNPJ:"
          value={formFields?.cpfCnpj ? formatCpfCnpj(formFields.cpfCnpj) : ''}
          disabled
        />
        <Field label="Preposto:" value={formFields?.preposto ?? ''} disabled />
        <Field label="Identificação Interna:" value={formFields?.identificacaoInterna ?? ''} disabled />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mt: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field label="Filial Responsável:" value={formFields?.filialResponsavel ?? ''} disabled />
        <Field label="Ramos de Atividade:" value={formFields?.ramosAtividade ?? ''} disabled />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          mt: 'clamp(10px, 0.8vw, 22px)',
        }}
      >
        <Field label="Comercial Principal:" value={formFields?.usuarioPrincipal ?? ''} disabled />
        <Field label="Telemarketing:" value={formFields?.usuarioTelemarketing ?? ''} disabled />
      </Box>

      <Box sx={{ mt: 'clamp(10px, 0.8vw, 22px)' }}>
        <Field
          label="Observação:"
          value={formFields?.observacao ?? ''}
          disabled
          multiline
          minRows={4}
        />
      </Box>
    </>
  );
}

IdentificacaoCliente.propTypes = {
  formFields: PropTypes.shape({
    idCliente: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    nomeCliente: PropTypes.string,
    origemLead: PropTypes.string,
    statusCliente: PropTypes.string,
    nomeFantasia: PropTypes.string,
    inscricaoEstadual: PropTypes.string,
    inscricaoMunicipal: PropTypes.string,
    categoria: PropTypes.string,
    cpfCnpj: PropTypes.string,
    preposto: PropTypes.string,
    identificacaoInterna: PropTypes.string,
    filialResponsavel: PropTypes.string,
    ramosAtividade: PropTypes.string,
    usuarioPrincipal: PropTypes.string,
    usuarioTelemarketing: PropTypes.string,
    observacao: PropTypes.string,
  }),
};

IdentificacaoCliente.defaultProps = {
  formFields: null,
};
